import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  User,
  Receipt,
  MapPin,
  Phone,
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
  Zap,
  Check,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const EMPTY = {
  firm_name: "",
  authorised_person: "",
  gstin: "",
  billing_address: "",
  delivery_address: "",
  phone: "",
};

export default function Register() {
  const { user, sendOtp, verifyOtp, checkPhone } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [sameAddress, setSameAddress] = useState(true);
  const [step, setStep] = useState("details"); // details | otp
  const [otp, setOtp] = useState("");
  const [debugOtp, setDebugOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.firm_name.trim()) return "Firm name is required";
    if (!form.authorised_person.trim()) return "Authorised person name is required";
    if (form.phone.length !== 10) return "Enter a valid 10-digit mobile number";
    if (form.gstin && !GSTIN_RE.test(form.gstin.trim().toUpperCase()))
      return "Invalid GSTIN format (e.g. 27AAAPL1234C1Z5)";
    if (!form.billing_address.trim() || form.billing_address.trim().length < 10)
      return "Enter a complete billing address";
    if (!sameAddress && (!form.delivery_address.trim() || form.delivery_address.trim().length < 10))
      return "Enter a complete delivery address (or tick 'same as billing')";
    return null;
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSending(true);
    try {
      // Warn if this mobile is already registered
      const exists = await checkPhone(form.phone);
      if (exists) {
        toast.error("This mobile is already registered. Please sign in instead.");
        setSending(false);
        return;
      }
      const data = await sendOtp(form.phone);
      setDebugOtp(data.otp_debug || "");
      setStep("otp");
      if (data.otp_debug) {
        toast.success(`OTP sent to +${form.phone}`, {
          description: `Demo OTP: ${data.otp_debug}`,
        });
      } else {
        toast.success(`OTP sent to +${form.phone} via SMS`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (code) => {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      const delivery = sameAddress ? form.billing_address : form.delivery_address;
      await verifyOtp(form.phone, code, {
        firm_name: form.firm_name.trim(),
        authorised_person: form.authorised_person.trim(),
        gstin: form.gstin.trim().toUpperCase() || undefined,
        billing_address: form.billing_address.trim(),
        delivery_address: delivery.trim(),
      });
      toast.success("Account created — welcome!");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
      setOtp("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-100">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-white text-zinc-900 flex-col p-12 overflow-hidden border-r-2 border-zinc-900">
        <div className="caution-stripe absolute top-0 left-0 right-0 h-2" />
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-[#FF5A00] flex items-center justify-center border-2 border-zinc-900">
            <Zap className="w-6 h-6 text-zinc-900" strokeWidth={2.5} />
          </div>
          <div className="font-display text-3xl font-black tracking-tight">
            UDYOG<span className="text-[#FF5A00]">.BIZ</span>
          </div>
        </div>

        <div className="mt-16 max-w-lg">
          <div className="tag text-[#FF5A00] mb-4">INDUSTRIAL COMMERCE · REGISTRATION</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black leading-[1.02] tracking-tight">
            Register your firm.<br />
            Start bulk sourcing in{" "}
            <span className="text-[#FF5A00]">minutes</span>.
          </h1>
          <p className="mt-6 text-zinc-500 text-lg font-light max-w-md">
            One-time registration. Your GSTIN, addresses and firm details are used
            for tax invoices and shipping.
          </p>
        </div>

        <ul className="mt-auto space-y-3 text-zinc-700">
          {[
            "GST-compliant tax invoices auto-emailed",
            "Buy today, delivered tomorrow to your factory gate",
            "Live price trends across 12+ raw materials",
            "Zero platform fees for MSME buyers",
          ].map((b, i) => (
            <li key={i} className="flex items-center gap-3">
              <div className="w-6 h-6 bg-[#FF5A00] border-2 border-zinc-900 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
              <span className="font-medium">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-start p-6 lg:p-10 grid-bg overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-[#FF5A00] flex items-center justify-center border-2 border-zinc-900">
              <Zap className="w-5 h-5 text-zinc-900" strokeWidth={2.5} />
            </div>
            <div className="font-display text-2xl font-black tracking-tight">
              UDYO<span className="text-[#FF5A00]">GG</span>
            </div>
          </div>

          <div className="bg-white border-2 border-zinc-900 p-6 lg:p-7 hard-shadow-sm">
            <div className="tag">
              {step === "details" ? "STEP 01 / 02" : "STEP 02 / 02"}
            </div>
            <h2 className="font-display text-3xl font-black mt-1 tracking-tight">
              {step === "details" ? "Register your firm" : "Verify mobile"}
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              {step === "details"
                ? "Details are used on GST invoices and shipping."
                : `Sent to +${form.phone}. Valid for 5 minutes.`}
            </p>

            {step === "details" ? (
              <form onSubmit={handleContinue} className="mt-6 space-y-4" data-testid="register-form">
                <div>
                  <label className="tag flex items-center gap-1 mb-1.5">
                    <Building2 className="w-3 h-3" /> FIRM NAME
                  </label>
                  <input
                    data-testid="register-firm-name"
                    value={form.firm_name}
                    onChange={onChange("firm_name")}
                    placeholder="Acme Polymers Pvt Ltd"
                    className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2.5"
                  />
                </div>

                <div>
                  <label className="tag flex items-center gap-1 mb-1.5">
                    <User className="w-3 h-3" /> AUTHORISED PERSON
                  </label>
                  <input
                    data-testid="register-authorised-person"
                    value={form.authorised_person}
                    onChange={onChange("authorised_person")}
                    placeholder="Rakesh Sharma"
                    className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2.5"
                  />
                </div>

                <div>
                  <label className="tag flex items-center gap-1 mb-1.5">
                    <Phone className="w-3 h-3" /> MOBILE NUMBER
                  </label>
                  <div className="flex border-2 border-zinc-300 focus-within:border-[#FF5A00]">
                    <span className="bg-zinc-900 text-white px-3 flex items-center font-mono-data text-sm">
                      +91
                    </span>
                    <input
                      data-testid="register-phone"
                      type="tel"
                      inputMode="numeric"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                        })
                      }
                      placeholder="9876543210"
                      className="flex-1 outline-none px-3 py-2.5 font-mono-data"
                    />
                  </div>
                </div>

                <div>
                  <label className="tag flex items-center gap-1 mb-1.5">
                    <Receipt className="w-3 h-3" /> GSTIN (OPTIONAL)
                  </label>
                  <input
                    data-testid="register-gstin"
                    value={form.gstin}
                    onChange={(e) =>
                      setForm({ ...form, gstin: e.target.value.toUpperCase().slice(0, 15) })
                    }
                    placeholder="27AAAPL1234C1Z5"
                    maxLength={15}
                    className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2.5 font-mono-data uppercase tracking-widest"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Leave blank if not registered. Required for GST-input claim on invoices.
                  </p>
                </div>

                <div>
                  <label className="tag flex items-center gap-1 mb-1.5">
                    <MapPin className="w-3 h-3" /> BILLING ADDRESS
                  </label>
                  <textarea
                    data-testid="register-billing-address"
                    value={form.billing_address}
                    onChange={onChange("billing_address")}
                    rows={3}
                    placeholder="Plot no, street, area, landmark, city, PIN"
                    className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2.5"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    data-testid="register-same-address-checkbox"
                    type="checkbox"
                    checked={sameAddress}
                    onChange={(e) => setSameAddress(e.target.checked)}
                    className="w-4 h-4 accent-[#FF5A00]"
                  />
                  <span className="text-sm">
                    Delivery address is <strong>same as billing</strong>
                  </span>
                </label>

                {!sameAddress && (
                  <div>
                    <label className="tag flex items-center gap-1 mb-1.5">
                      <MapPin className="w-3 h-3" /> DELIVERY ADDRESS
                    </label>
                    <textarea
                      data-testid="register-delivery-address"
                      value={form.delivery_address}
                      onChange={onChange("delivery_address")}
                      rows={3}
                      placeholder="Factory / warehouse address"
                      className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2.5"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="register-continue-button"
                  className="w-full bg-[#FF5A00] text-white font-mono-data tracking-widest text-sm py-3.5 border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                >
                  {sending ? (
                    "SENDING OTP..."
                  ) : (
                    <>
                      CONTINUE <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <div className="text-center pt-3 border-t border-zinc-200">
                  <p className="text-sm text-zinc-500">
                    Already registered?{" "}
                    <Link
                      to="/login"
                      data-testid="go-to-login-link"
                      className="font-semibold text-[#FF5A00] hover:underline"
                    >
                      Sign in →
                    </Link>
                  </p>
                </div>
              </form>
            ) : (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="tag block mb-3">6-DIGIT CODE</label>
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      if (v.length === 6) handleVerify(v);
                    }}
                    data-testid="register-otp-input"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="w-12 h-14 text-xl font-mono-data font-bold border-2 border-zinc-900 rounded-none"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {debugOtp && (
                  <div
                    data-testid="register-debug-otp-banner"
                    className="bg-amber-50 border-2 border-amber-400 px-3 py-2 font-mono-data text-xs flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4 text-amber-700" />
                    <span>
                      DEMO OTP: <strong>{debugOtp}</strong>
                    </span>
                  </div>
                )}

                <button
                  onClick={() => handleVerify(otp)}
                  disabled={verifying || otp.length !== 6}
                  data-testid="register-verify-button"
                  className="w-full bg-zinc-900 text-white font-mono-data tracking-widest text-sm py-4 border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50"
                >
                  {verifying ? "CREATING ACCOUNT..." : "VERIFY & CREATE ACCOUNT"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep("details");
                    setOtp("");
                    setDebugOtp("");
                  }}
                  className="w-full flex items-center justify-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 py-1"
                  data-testid="register-back-button"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to details
                </button>
              </div>
            )}
          </div>

          <p className="mt-4 text-xs text-zinc-500 font-mono-data text-center">
            By registering you agree to Udyogg's terms & privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
